import { apiClient } from './apiClient';
import type { UtilisationReportData, UtilisationSummary } from '@/types/utilisation';

export interface UtilisationQueryParams {
  dateFrom?: Date;
  dateTo?: Date;
  employeeIds?: string[];
  departments?: string[];
  projectIds?: string[];
  includeNonBillable?: boolean;
  groupBy?: 'employee' | 'department' | 'project' | 'week';
}

/**
 * Build query string from parameters
 * Arrays are repeated as multiple query parameters (e.g., employeeIds=E1&employeeIds=E2)
 */
function buildQueryString(params: UtilisationQueryParams): string {
  const queryParts: string[] = [];

  if (params.dateFrom) {
    queryParts.push(`dateFrom=${params.dateFrom.toISOString().split('T')[0]}`);
  }
  if (params.dateTo) {
    queryParts.push(`dateTo=${params.dateTo.toISOString().split('T')[0]}`);
  }

  if (params.employeeIds && params.employeeIds.length > 0) {
    params.employeeIds.forEach(id => {
      queryParts.push(`employeeIds=${encodeURIComponent(id)}`);
    });
  }
  if (params.departments && params.departments.length > 0) {
    params.departments.forEach(dept => {
      queryParts.push(`departments=${encodeURIComponent(dept)}`);
    });
  }
  if (params.projectIds && params.projectIds.length > 0) {
    params.projectIds.forEach(id => {
      queryParts.push(`projectIds=${encodeURIComponent(id)}`);
    });
  }

  if (params.includeNonBillable !== undefined) {
    queryParts.push(`includeNonBillable=${params.includeNonBillable}`);
  }

  if (params.groupBy) {
    queryParts.push(`groupBy=${encodeURIComponent(params.groupBy)}`);
  }

  return queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
}

/**
 * Parse date strings in API response to Date objects
 */
function parseUtilisationReportDates(report: any): UtilisationReportData {
  return {
    ...report,
    trends: report.trends?.map((trend: any) => ({
      ...trend,
      weekEnding: trend.weekEnding ? new Date(trend.weekEnding) : trend.weekEnding,
    })) || [],
  };
}

/**
 * Utilisation API service for utilisation report endpoints
 */
export const utilisationApi = {
  /**
   * Get complete utilisation report with all metrics
   */
  async getUtilisationReport(params: UtilisationQueryParams = {}): Promise<UtilisationReportData> {
    const queryString = buildQueryString(params);
    const response = await apiClient.get<any>(`/reports/utilisation${queryString}`);
    return parseUtilisationReportDates(response);
  },

  /**
   * Get utilisation summary only (lightweight)
   */
  async getUtilisationSummary(params: Omit<UtilisationQueryParams, 'groupBy'>): Promise<UtilisationSummary> {
    const queryString = buildQueryString(params);
    return apiClient.get<UtilisationSummary>(`/reports/utilisation/summary${queryString}`);
  },
};
